/**
 * Journal metrics from OpenAlex's `sources` endpoint.
 *
 * These are OpenAlex's own numbers, computed from OpenAlex's citation graph. They are not
 * Clarivate's Journal Impact Factor (a Journal Citation Reports figure) and not Elsevier's
 * CiteScore (a Scopus figure), and they do not match either. `2yr_mean_citedness` is
 * described by OpenAlex as an impact-factor-style two-year mean; it carries no
 * Clarivate licence and no JCR editorial rules about which items count.
 *
 * A lookup by ISSN is a free "singleton" call; a name search is one list request.
 */

import { fetchJson } from "@/lib/tool-kit/reference-tools/network";
import { OPENALEX_MAILTO } from "@/lib/tool-kit/research-discovery/citation-chaser";

const API = "https://api.openalex.org/sources";
const FIELDS = [
  "id",
  "issn_l",
  "issn",
  "display_name",
  "alternate_titles",
  "type",
  "host_organization_name",
  "country_code",
  "homepage_url",
  "works_count",
  "oa_works_count",
  "cited_by_count",
  "summary_stats",
  "is_oa",
  "is_in_doaj",
  "is_in_doaj_since_year",
  "is_core",
  "is_high_oa_rate",
  "listed_in",
  "first_publication_year",
  "last_publication_year",
  "apc_prices",
  "apc_usd",
  "counts_by_year",
].join(",");

export const SEARCH_RESULTS = 8;
const RE_ISSN = /^(\d{4})-?(\d{3}[\dXx])$/;
const RE_ISSN_IN_TEXT = /\b(\d{4}-\d{3}[\dXx])\b/;

/** "00278424" / "0027-8424" → "0027-8424", or null when it is not shaped like an ISSN. */
export function normalizeIssn(text: string): string | null {
  const match = RE_ISSN.exec(text.trim());
  return match ? `${match[1]}-${match[2].toUpperCase()}` : null;
}

/** An ISSN anywhere in the text — "ISSN 0027-8424" works too. */
export function findIssn(text: string): string | null {
  const direct = normalizeIssn(text);
  if (direct) {
    return direct;
  }
  const inText = RE_ISSN_IN_TEXT.exec(text);
  return inText ? normalizeIssn(inText[1]) : null;
}

/** ISO 3297 check digit: weights 8…2 over the first seven digits, modulo 11. */
export function issnCheckDigitValid(issn: string): boolean {
  const digits = issn.replace("-", "").toUpperCase();
  if (digits.length !== 8) {
    return false;
  }
  let sum = 0;
  for (let index = 0; index < 7; index++) {
    sum += Number(digits[index]) * (8 - index);
  }
  const remainder = (11 - (sum % 11)) % 11;
  const expected = remainder === 10 ? "X" : String(remainder);
  return digits[7] === expected;
}

export function issnUrl(issn: string): string {
  return `${API}/issn:${issn}?select=${FIELDS}&mailto=${encodeURIComponent(OPENALEX_MAILTO)}`;
}

export function searchUrl(query: string): string {
  return `${API}?search=${encodeURIComponent(query.trim())}&per-page=${SEARCH_RESULTS}&select=${FIELDS}&mailto=${encodeURIComponent(OPENALEX_MAILTO)}`;
}

/* ---------------------------------------------------------------- parsing */

export interface YearCount {
  year: number;
  works: number;
  oaWorks: number;
  citations: number;
}

export interface Apc {
  price: number;
  currency: string;
}

export interface JournalMetrics {
  /** "S125754415" */
  id: string;
  name: string;
  alternateTitles: string[];
  type: string;
  publisher: string;
  country: string;
  homepage: string;
  issnL: string;
  issns: string[];
  works: number;
  oaWorks: number;
  citations: number;
  hIndex: number | null;
  i10Index: number | null;
  /** OpenAlex's impact-factor-style 2-year mean; null when not reported. */
  meanCitedness: number | null;
  fullyOa: boolean;
  inDoaj: boolean;
  doajSince: number | null;
  inCore: boolean;
  highOaRate: boolean;
  inMedline: boolean;
  firstYear: number | null;
  lastYear: number | null;
  apcs: Apc[];
  apcUsd: number | null;
  years: YearCount[];
}

function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function count(value: unknown): number {
  return num(value) ?? 0;
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map(str).filter((item) => item !== "")
    : [];
}

function parseYears(raw: unknown): YearCount[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .flatMap((entry) => {
      const row = (entry ?? {}) as Record<string, unknown>;
      const year = num(row.year);
      return year === null
        ? []
        : [
            {
              year,
              works: count(row.works_count),
              oaWorks: count(row.oa_works_count),
              citations: count(row.cited_by_count),
            },
          ];
    })
    .sort((a, b) => b.year - a.year);
}

function parseApcs(raw: unknown): Apc[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((entry) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    const price = num(row.price);
    const currency = str(row.currency);
    return price === null || currency === "" ? [] : [{ price, currency }];
  });
}

/** One OpenAlex source as journal metrics; null without an ID and a name. */
export function parseSource(raw: unknown): JournalMetrics | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const source = raw as Record<string, unknown>;
  const id = str(source.id).split("/").at(-1) ?? "";
  const name = str(source.display_name);
  if (id === "" || name === "") {
    return null;
  }
  const stats = (source.summary_stats ?? {}) as Record<string, unknown>;
  const issnL = str(source.issn_l);
  return {
    id,
    name,
    alternateTitles: strings(source.alternate_titles).slice(0, 4),
    type: str(source.type),
    publisher: str(source.host_organization_name),
    country: str(source.country_code).toUpperCase(),
    homepage: str(source.homepage_url),
    issnL,
    issns: strings(source.issn),
    works: count(source.works_count),
    oaWorks: count(source.oa_works_count),
    citations: count(source.cited_by_count),
    hIndex: num(stats.h_index),
    i10Index: num(stats.i10_index),
    meanCitedness: num(stats["2yr_mean_citedness"]),
    fullyOa: source.is_oa === true,
    inDoaj: source.is_in_doaj === true,
    doajSince: num(source.is_in_doaj_since_year),
    inCore: source.is_core === true,
    highOaRate: source.is_high_oa_rate === true,
    inMedline: strings(source.listed_in).includes("medline"),
    firstYear: num(source.first_publication_year),
    lastYear: num(source.last_publication_year),
    apcs: parseApcs(source.apc_prices),
    apcUsd: num(source.apc_usd),
    years: parseYears(source.counts_by_year),
  };
}

/** A search response as journals, in OpenAlex's relevance order. */
export function parseSourceList(body: unknown): JournalMetrics[] {
  const results = (body as { results?: unknown } | null)?.results;
  if (!Array.isArray(results)) {
    return [];
  }
  return results.flatMap((raw) => {
    const parsed = parseSource(raw);
    return parsed ? [parsed] : [];
  });
}

/* ------------------------------------------------------------- derivations */

/** Open-access works as a percentage of all works, or null with no works. */
export function oaShare(journal: JournalMetrics): number | null {
  return journal.works > 0 ? (journal.oaWorks / journal.works) * 100 : null;
}

/** Mean citations per work over the journal's whole record. */
export function citationsPerWork(journal: JournalMetrics): number | null {
  return journal.works > 0 ? journal.citations / journal.works : null;
}

/** The last `span` years of counts, newest first. */
export function recentYears(journal: JournalMetrics, span = 10): YearCount[] {
  return journal.years.slice(0, span);
}

/** Open-access share of one year's works, as a percentage; null with no works. */
export function yearOaShare(row: YearCount): number | null {
  return row.works > 0 ? (row.oaWorks / row.works) * 100 : null;
}

/* --------------------------------------------------------------- lookup */

export type LookupProblem =
  | "tooShort"
  | "notFound"
  | "noMatches"
  | "rateLimited"
  | "failed";

export type LookupResult =
  | { ok: true; journals: JournalMetrics[]; badCheckDigit: boolean }
  | { ok: false; problem: LookupProblem };

function problemFor(reason: string, byIssn: boolean): LookupProblem {
  if (reason === "not-found") {
    return byIssn ? "notFound" : "noMatches";
  }
  return reason === "rate-limited" ? "rateLimited" : "failed";
}

/**
 * Look a journal up by ISSN (one free call) or by title (one list call). An ISSN anywhere
 * in the text wins. Rethrows when `signal` aborts.
 */
export async function lookUpJournal(
  text: string,
  signal: AbortSignal
): Promise<LookupResult> {
  const query = text.trim();
  const issn = findIssn(query);
  if (!issn && query.length < 2) {
    return { ok: false, problem: "tooShort" };
  }
  const response = await fetchJson<unknown>(
    issn ? issnUrl(issn) : searchUrl(query),
    { signal, retries: 1 }
  );
  if (!response.ok) {
    return { ok: false, problem: problemFor(response.reason, issn !== null) };
  }
  const journals = issn
    ? parseSourceList({ results: [response.value] })
    : parseSourceList(response.value);
  if (journals.length === 0) {
    return { ok: false, problem: issn ? "notFound" : "noMatches" };
  }
  return {
    ok: true,
    journals,
    badCheckDigit: issn !== null && !issnCheckDigitValid(issn),
  };
}
